import React, { useState, useEffect, useCallback } from "react";
import Form from "react-bootstrap/Form";
import _ from "lodash";
import uuid from "react-uuid";
import Loading from "../../../common/components/loading/loading";
import { getUoms } from "../../../common/services/markets/uomService";
import SearchBox from "../../../common/components/search/searchBox";
import UomsTable from "./uomsTable";
import { useTranslation } from "react-i18next";

const Uoms = ({ onClick, allowEdit }) => {
  const { t } = useTranslation(["markets"]);
  const [uoms, setUoms] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [sortColumn, setSortColumn] = useState({ path: "name", order: "asc" });
  const [isLoading, setIsLoading] = useState(true);
  const [paginationState, setPaginationState] = useState({
    currentPage: 0,
    pageSize: 20,
  });

  useEffect(() => {
    (async () => {
      try {
        setUoms(await getUoms());
      } catch (error) {
        console.error("Failed to fetch UOMs:", error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleUomUpdated = useCallback((uom) => {
    setUoms((prevUoms) => {
      const updatedUoms = [...prevUoms];
      const index = updatedUoms.findIndex((u) => u.uomId === uom.uomId);
      if (index > -1) {
        updatedUoms.splice(index, 1);
      }
      updatedUoms.push(uom);
      return updatedUoms;
    });
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setPaginationState((prev) => ({ ...prev, currentPage: 0 }));
  };

  const handleSort = (colId, sort) => {
    if (colId && sort) {
      setSortColumn({ path: colId, order: sort });
    }
  };

  const handleToggleActiveOnly = () => {
    setActiveOnly((prev) => !prev);
    setPaginationState((prev) => ({ ...prev, currentPage: 0 }));
  };

  const handleUomSelected = (uom, isNew = false) => {
    onClick(uom, handleUomUpdated, isNew);
  };

  const handlePaginationChanged = (params) => {
    if (params && params.api) {
      setPaginationState({
        currentPage: params.api.paginationGetCurrentPage(),
        pageSize: params.api.paginationGetPageSize(),
      });
    }
  };

  const getFilteredData = () => {
    let filtered = uoms;
    if (activeOnly) filtered = filtered.filter((u) => u.active);
    if (searchQuery)
      filtered = filtered.filter(
        (u) =>
          (u.name != null &&
            u.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (u.code != null &&
            u.code.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);
    return { totalCount: filtered.length, data: sorted };
  };

  const handleAddUom = () => {
    // Each draft needs its own tab identity, including after a previous save.
    handleUomSelected({ uomId: uuid(), name: t("NewUom") }, true);
  };

  if (isLoading) return <Loading />;

  const { totalCount, data } = getFilteredData();

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          <SearchBox value={searchQuery} onChange={handleSearch} />
        </div>
        <div className="col">
          <Form.Switch
            className="switch"
            type="switch"
            label={t("ActiveOnly")}
            checked={activeOnly}
            value={activeOnly}
            onChange={handleToggleActiveOnly}
          />
        </div>
        <div className="col">
          {allowEdit && (
            <button
              onClick={handleAddUom}
              className="btn btn-sm btn-primary float-sm-right"
            >
              {t("AddUom")}
            </button>
          )}
        </div>
      </div>
      <p>{t("RetrievedUom", { count: totalCount })}</p>
      <UomsTable
        uoms={data}
        onSort={handleSort}
        onRowClick={handleUomSelected}
        onPaginationChanged={handlePaginationChanged}
        paginationState={paginationState}
      />
    </div>
  );
};

export default Uoms;
